import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getWhatsAppDashboard, getWhatsAppErrorMessage } from "../../services/whatsappApi";
import {
  campaignStatusClass,
  formatCampaignDateMedium,
} from "../../utils/whatsappCampaign";
import "./whatsapp.css";
import "./whatsapp-dashboard.css";
import WhatsAppPageShell from "./WhatsAppPageShell";

const STATUS_CHART_COLORS = {
  COMPLETED: "#25d366",
  IN_PROGRESS: "#3b82f6",
  PENDING: "#f59e0b",
  FAILED: "#ea4335",
};

function reportError(err, fallback) {
  const msg = getWhatsAppErrorMessage(err, fallback);
  toast.error(msg);
  return msg;
}

function StatusBadge({ status }) {
  return (
    <span className={`wa-status-badge ${campaignStatusClass(status)}`}>
      {status || "—"}
    </span>
  );
}

function StatCard({ label, value, icon, tone = "default", loading }) {
  return (
    <div className="wa-dashboard-stat-card">
      {loading ? (
        <div className="whatsapp-skeleton" style={{ height: 56 }} />
      ) : (
        <>
          <div className={`wa-dashboard-stat-icon wa-dashboard-stat-icon--${tone}`}>
            <Icon icon={icon} width={24} />
          </div>
          <div>
            <p className="wa-dashboard-stat-value">{Number(value ?? 0).toLocaleString()}</p>
            <p className="wa-dashboard-stat-label">{label}</p>
          </div>
        </>
      )}
    </div>
  );
}

function campaignField(row, ...keys) {
  for (const key of keys) {
    const value = row?.[key];
    if (value != null && value !== "") return value;
  }
  return null;
}

function buildStatusChartData(campaignsByStatus = {}) {
  return Object.entries(STATUS_CHART_COLORS)
    .map(([key, color]) => ({
      name: key.replace(/_/g, " "),
      key,
      value: Number(campaignsByStatus[key]) || 0,
      color,
    }))
    .filter((row) => row.value > 0);
}

export default function WhatsAppDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getWhatsAppDashboard();
      setData(res?.data ?? res);
    } catch (err) {
      reportError(err, "Failed to load dashboard");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const messageStats = data?.messageStats || {};
  const campaignsByStatus = data?.campaignsByStatus || {};
  const dailyStats = Array.isArray(data?.dailyStats) ? data.dailyStats : [];
  const recentCampaigns = Array.isArray(data?.recentCampaigns)
    ? data.recentCampaigns
    : [];

  const statusChartData = useMemo(
    () => buildStatusChartData(campaignsByStatus),
    [campaignsByStatus],
  );

  const statusChartTotal = statusChartData.reduce((sum, row) => sum + row.value, 0);

  const messageChartData = useMemo(
    () =>
      dailyStats.slice(-30).map((row) => ({
        date: String(row.date || "").slice(5),
        sent: Number(row.sent) || 0,
        delivered: Number(row.delivered) || 0,
        failed: Number(row.failed) || 0,
      })),
    [dailyStats],
  );

  return (
    <WhatsAppPageShell className="whatsapp-page-shell--panel">
      <div className="whatsapp-inbox-root">
        <div className="whatsapp-page-panel wa-dashboard-page">
          <div className="wa-dashboard-tabs">
            <button type="button" className="wa-dashboard-tab active">
              Dashboard Overview
            </button>
          </div>

          <div className="wa-dashboard-body">
            <div className="wa-dashboard-stat-grid">
              <StatCard
                label="Total Campaigns"
                value={data?.totalCampaigns}
                icon="mdi:whatsapp"
                loading={loading}
              />
              <StatCard
                label="Messages Sent"
                value={messageStats.sent}
                icon="solar:check-read-linear"
                loading={loading}
              />
              <StatCard
                label="Messages Delivered"
                value={messageStats.delivered}
                icon="solar:check-read-bold"
                loading={loading}
              />
              <StatCard
                label="Failed Messages"
                value={messageStats.failed}
                icon="solar:danger-triangle-line-duotone"
                tone="danger"
                loading={loading}
              />
            </div>

            <div className="wa-dashboard-charts-row">
              <div className="wa-dashboard-card">
                <h2 className="wa-dashboard-card-title">Campaign Status Distribution</h2>
                {loading ? (
                  <div className="whatsapp-skeleton wa-dashboard-chart-skeleton" />
                ) : statusChartData.length === 0 ? (
                  <div className="whatsapp-list-empty">No campaign data yet.</div>
                ) : (
                  <div className="wa-dashboard-pie-wrap">
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie
                          data={statusChartData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={0}
                          outerRadius={100}
                          paddingAngle={statusChartData.length > 1 ? 2 : 0}
                          stroke="#fff"
                          strokeWidth={2}
                        >
                          {statusChartData.map((entry) => (
                            <Cell key={entry.key} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name) => [
                            `${value} (${statusChartTotal ? Math.round((value / statusChartTotal) * 1000) / 10 : 0}%)`,
                            name,
                          ]}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="wa-dashboard-pie-legend">
                      {statusChartData.map((entry) => (
                        <div key={entry.key} className="wa-dashboard-pie-legend-item">
                          <span
                            className="wa-dashboard-pie-dot"
                            style={{ background: entry.color }}
                          />
                          <span>{entry.name}</span>
                          <span className="wa-dashboard-pie-legend-value">
                            {statusChartTotal
                              ? `${Math.round((entry.value / statusChartTotal) * 1000) / 10}%`
                              : "0%"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="wa-dashboard-card">
                <h2 className="wa-dashboard-card-title">
                  Message Statistics (Last 30 Days)
                </h2>
                {loading ? (
                  <div className="whatsapp-skeleton wa-dashboard-chart-skeleton" />
                ) : messageChartData.length === 0 ? (
                  <div className="whatsapp-list-empty">No message stats yet.</div>
                ) : (
                  <div className="wa-dashboard-line-wrap">
                    <ResponsiveContainer width="100%" height={260}>
                      <LineChart
                        data={messageChartData}
                        margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e9edef" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis
                          tick={{ fontSize: 11 }}
                          label={{
                            value: "Message Count",
                            angle: -90,
                            position: "insideLeft",
                            style: { fontSize: 11, fill: "#667781" },
                          }}
                        />
                        <Tooltip />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="sent"
                          stroke="#25d366"
                          strokeWidth={2}
                          dot={false}
                          name="Sent"
                        />
                        <Line
                          type="monotone"
                          dataKey="delivered"
                          stroke="#128c7e"
                          strokeWidth={2}
                          dot={false}
                          name="Delivered"
                        />
                        <Line
                          type="monotone"
                          dataKey="failed"
                          stroke="#ea4335"
                          strokeWidth={2}
                          dot={false}
                          name="Failed"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>

            <div className="wa-dashboard-card wa-dashboard-recent-card">
              <div className="wa-dashboard-recent-head">
                <h2 className="wa-dashboard-card-title mb-0">Recent Campaigns</h2>
                <Link to="/whatsapp/campaigns" className="wa-dashboard-view-all-btn">
                  View All
                </Link>
              </div>

              {loading ? (
                <div className="whatsapp-skeleton" style={{ height: 200, marginTop: 16 }} />
              ) : recentCampaigns.length === 0 ? (
                <div className="whatsapp-list-empty">No recent campaigns.</div>
              ) : (
                <div className="whatsapp-contacts-table-wrap wa-dashboard-table-wrap">
                  <table className="whatsapp-contacts-table wa-dashboard-campaigns-table">
                    <thead>
                      <tr>
                        <th>Campaign Name</th>
                        <th>Phonebook</th>
                        <th>Status</th>
                        <th>Sent</th>
                        <th>Delivered</th>
                        <th>Read</th>
                        <th>Failed</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentCampaigns.map((row) => {
                        const title = campaignField(row, "title") || "—";
                        const templateName =
                          campaignField(row, "templateName", "template_name") || "";
                        const phonebook =
                          campaignField(
                            row,
                            "phonebookName",
                            "phonebook_name",
                            "phonebook",
                          ) || "—";
                        return (
                          <tr key={row.id}>
                            <td>
                              <div className="wa-dashboard-campaign-name">{title}</div>
                              {templateName ? (
                                <div className="wa-dashboard-campaign-template">
                                  {templateName}
                                </div>
                              ) : null}
                            </td>
                            <td>{phonebook}</td>
                            <td>
                              <StatusBadge status={row.status} />
                            </td>
                            <td>{row.sentCount ?? row.sent_count ?? 0}</td>
                            <td>{row.deliveredCount ?? row.delivered_count ?? 0}</td>
                            <td>{row.readCount ?? row.read_count ?? 0}</td>
                            <td className="wa-dashboard-failed-cell">
                              {row.failedCount ?? row.failed_count ?? 0}
                            </td>
                            <td>
                              {formatCampaignDateMedium(
                                row.createdAt ?? row.created_at,
                              )}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="wa-page-toolbar-btn-outline wa-dashboard-details-btn"
                                onClick={() =>
                                  navigate(`/whatsapp/campaigns/${row.id}`)
                                }
                              >
                                Details
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </WhatsAppPageShell>
  );
}
